'use client';

import { connectUseCaseHref } from '@/@connect/use-cases';
import { cn, Link, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { DISABLED_TOOLTIP } from '../access/resolve-access';
import { useHoverIntent } from '../hooks/use-hover-intent';
import type { ResolvedNavSection } from '../hooks/use-nav-sections';
import { EXPLORE_CHANNELS } from '../explore-channels';

interface NavHoverPeekProps {
    section: ResolvedNavSection;
    onClose: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onOpenSynthesize: () => void;
}

function joinChildNames(names: string[]): string {
    if (names.length === 0) return '';
    if (names.length === 1) return `${names[0]}.`;
    if (names.length === 2) return `${names[0]} and ${names[1]}.`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}.`;
}

/**
 * Flyout shown when hovering a collapsed-rail section icon, so its children
 * are reachable without expanding the whole sidebar. Only rendered for
 * sections (Home has no children, so it never shows one).
 *
 * Connect gets a richer two-pane body (channel list +
 * detail actions) matching the design source of record
 * (`MFour Studio JTBD v15.dc.html`, the `peekIsAud` flyout) instead of the
 * generic child list every other section uses.
 */
export function NavHoverPeek({
    section,
    onClose,
    onMouseEnter,
    onMouseLeave,
    onOpenSynthesize,
}: NavHoverPeekProps) {
    const isExplore = section.key === 'explore';

    // A fanned-out row's own popover (e.g. Dashboards' flyout) is portaled
    // outside this div, so once the cursor is hovering it directly, this
    // div's own onMouseEnter/onMouseLeave below never fire again — nothing
    // would otherwise stop nav-shell's own close timer from firing under
    // the user mid-interaction. Each row reports its open state here instead
    // of this component trying to mirror every individual mouse event
    // (which is what used to cause this): as long as any row reports open,
    // the effect below keeps re-cancelling the outer close timer.
    const [openNestedFlyouts, setOpenNestedFlyouts] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const setNestedFlyoutOpen = useCallback((name: string, isOpen: boolean) => {
        setOpenNestedFlyouts((prev) => {
            if (prev.has(name) === isOpen) return prev;
            const next = new Set(prev);
            if (isOpen) next.add(name);
            else next.delete(name);
            return next;
        });
    }, []);
    useEffect(() => {
        if (openNestedFlyouts.size > 0) onMouseEnter();
    }, [openNestedFlyouts, onMouseEnter]);

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={cn('rounded-2xl border border-divider bg-background shadow-xl', {
                'w-64': !isExplore,
                'w-[440px]': isExplore,
            })}
        >
            <div className="flex items-start gap-2 p-3">
                <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
                    style={{
                        background: section.chipBg ?? `${section.color}22`,
                        color: section.iconColor ?? section.color,
                    }}
                >
                    <Icon icon={`lucide:${section.icon}`} width={13} height={13} />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-[13px] font-bold leading-tight text-foreground">
                        {section.label}
                    </p>
                    <p className="mt-0.5 text-[11.5px] leading-snug text-default-500">
                        {isExplore
                            ? 'Ten consumer data channels — explore any, merge them all.'
                            : joinChildNames(section.children.map((c) => c.name))}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-default-400 hover:bg-[#F3F8FF]"
                >
                    <Icon icon="lucide:x" width={13} height={13} />
                </button>
            </div>
            <div className="mx-3 border-t border-divider" />
            {isExplore ? (
                <ExploreChannelPicker onNavigate={onClose} onOpenSynthesize={onOpenSynthesize} />
            ) : (
                <div className="flex flex-col gap-0.5 p-2">
                    {section.children.map((child) => (
                        <PeekChildRow
                            key={child.name}
                            child={child}
                            onNavigate={onClose}
                            onFlyoutOpenChange={(isOpen) => setNestedFlyoutOpen(child.name, isOpen)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

NavHoverPeek.displayName = 'NavHoverPeek';

/** The two-pane channel list + detail-actions body, Explore-only. */
function ExploreChannelPicker({
    onNavigate,
    onOpenSynthesize,
}: {
    onNavigate: () => void;
    onOpenSynthesize: () => void;
}) {
    const router = useRouter();
    const [selectedKey, setSelectedKey] = useState(EXPLORE_CHANNELS[0].key);
    const selected = EXPLORE_CHANNELS.find((c) => c.key === selectedKey) ?? EXPLORE_CHANNELS[0];

    const launch = (useCaseId: string) => {
        router.push(connectUseCaseHref(useCaseId));
        onNavigate();
    };

    return (
        <>
            <div className="flex gap-3 p-2 pt-2">
                <div className="flex w-[168px] shrink-0 flex-col gap-0.5 border-r border-divider pr-2">
                    {EXPLORE_CHANNELS.map((channel) => {
                        const isSelected = channel.key === selectedKey;
                        return (
                            <button
                                key={channel.key}
                                type="button"
                                onMouseEnter={() => setSelectedKey(channel.key)}
                                onClick={() => setSelectedKey(channel.key)}
                                className={cn(
                                    'flex items-center gap-1.5 rounded-large px-1.5 py-1 text-left transition-colors',
                                    isSelected ? 'bg-[#F3F8FF]' : 'hover:bg-default-100',
                                )}
                            >
                                <span
                                    className="flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-md"
                                    style={{
                                        background: `${channel.color}22`,
                                        color: channel.color,
                                    }}
                                >
                                    <Icon icon={`lucide:${channel.icon}`} width={11} height={11} />
                                </span>
                                <span
                                    className={cn(
                                        'flex-1 truncate text-[12px] font-semibold leading-tight',
                                        isSelected ? 'text-primary-700' : 'text-foreground',
                                    )}
                                >
                                    {channel.name}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className="min-w-0 flex-1 pl-1">
                    <p className="text-[12.5px] font-bold leading-tight tracking-tight text-foreground">
                        {selected.name}
                    </p>
                    <p className="mt-0.5 text-[10.5px] leading-snug text-default-500">
                        {selected.tagline}
                    </p>
                    <div className="mt-2.5 flex flex-col gap-0.5 border-t border-divider pt-1">
                        {selected.actions.map((action) => (
                            <button
                                key={action.name}
                                type="button"
                                onClick={() => launch(action.useCaseId)}
                                className="flex items-center gap-2 rounded-large px-1.5 py-1.5 text-left transition-colors hover:bg-[#F3F8FF]"
                            >
                                <Icon
                                    icon={`lucide:${action.icon}`}
                                    width={13}
                                    height={13}
                                    className="shrink-0 text-default-500"
                                />
                                <span className="flex-1 truncate text-[12px] font-medium text-foreground">
                                    {action.name}
                                </span>
                                <Icon
                                    icon="lucide:arrow-right"
                                    width={11}
                                    height={11}
                                    className="shrink-0 text-default-400"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mx-3 border-t border-divider" />
            <div className="p-2">
                <button
                    type="button"
                    onClick={() => {
                        onOpenSynthesize();
                        onNavigate();
                    }}
                    className="flex w-full items-center gap-2.5 rounded-large px-2.5 py-2 text-left transition-colors hover:bg-[#F3F8FF]"
                >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#22CD89]/[0.14] text-[#159c67]">
                        <Icon icon="lucide:infinity" width={14} height={14} />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-semibold leading-tight text-foreground">
                            Synthesize channels
                        </span>
                        <span className="block text-[10.5px] leading-snug text-default-500">
                            Combine any of these into one insight
                        </span>
                    </span>
                    <Icon
                        icon="lucide:arrow-right"
                        width={14}
                        height={14}
                        className="shrink-0 text-default-400"
                    />
                </button>
            </div>
        </>
    );
}

function PeekChildRow({
    child,
    onNavigate,
    onFlyoutOpenChange,
}: {
    child: ResolvedNavSection['children'][number];
    onNavigate: () => void;
    /** Reports this row's own nested-popover open state up to `NavHoverPeek`,
     * so it can keep itself alive for as long as this is open — see the
     * `hasFlyout` branch below for why. */
    onFlyoutOpenChange: (isOpen: boolean) => void;
}) {
    // Closes on a short delay (see useHoverIntent) rather than instantly, so
    // the flyout doesn't flicker open-closed-open as the cursor crosses the
    // gap between this row and the portaled PopoverContent below.
    const { isOpen: isHovered, setOpen: setIsHovered, handlers: hoverHandlers } = useHoverIntent();
    const hasFlyout = !!child.flyout?.length;
    useEffect(() => {
        onFlyoutOpenChange(hasFlyout && isHovered);
        // Clear this row's reported-open state on unmount — if the peek
        // switches sections while this row's flyout is open, the row unmounts
        // mid-open and would otherwise linger in `openNestedFlyouts`, leaving
        // the keep-alive effect cancelling the peek's close timer forever so
        // it sticks open after the cursor has left.
        return () => onFlyoutOpenChange(false);
        // `onFlyoutOpenChange` is a fresh closure every render (it captures
        // `child.name`) — keying the effect on it would refire on every
        // parent re-render for no reason. `isHovered`/`hasFlyout` are the
        // only inputs that should ever trigger this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHovered, hasFlyout]);
    const inert = !!child.comingSoon || child.isDisabled || (!child.href && !hasFlyout);

    const content = (
        <>
            <span
                className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg',
                    inert ? 'bg-default-50' : 'bg-default-100',
                )}
            >
                <Icon
                    icon={`lucide:${child.icon}`}
                    width={14}
                    height={14}
                    className={inert ? 'text-default-300' : 'text-default-500'}
                />
            </span>
            <span
                className={cn(
                    'flex-1 text-[12px] font-semibold',
                    inert ? 'text-default-300' : 'text-default-700',
                )}
            >
                {child.name}
            </span>
            {!inert && (
                <Icon
                    icon="lucide:arrow-right"
                    width={14}
                    height={14}
                    className="text-default-500"
                />
            )}
        </>
    );

    const rowClasses = cn(
        'flex items-center gap-2.5 rounded-large px-2.5 py-2 hover:!opacity-100',
        {
            'hover:bg-[#F3F8FF]': !inert,
            'cursor-pointer': inert,
        },
    );

    if (inert) {
        return (
            <div className={rowClasses} aria-disabled>
                <Tooltip
                    content={
                        child.tooltip ?? DISABLED_TOOLTIP[child.disabledReason ?? 'coming-soon']
                    }
                    placement="right"
                    size="sm"
                    classNames={{ content: 'bg-[#192138] px-3 py-1 text-[11px] text-white' }}
                >
                    <div className="flex items-center gap-2.5">{content}</div>
                </Tooltip>
            </div>
        );
    }

    // No `href` of its own — hovering reveals a nested flyout of the links
    // it fans out to (e.g. Dashboards' Sigma dashboards), matching the
    // expanded nav's own `NavChildRow` (`nav-section.tsx`).
    //
    // This row's own Popover content is portaled (rendered outside
    // `NavHoverPeek`'s DOM subtree), so the outer flyout's mouseenter/
    // mouseleave — which only sees events on `NavHoverPeek`'s own root div —
    // never fires again once the cursor crosses into this nested popover.
    // The `onFlyoutOpenChange` effect above reports `isHovered` up to
    // `NavHoverPeek`, which keeps itself alive for as long as it stays true,
    // so this row's own open/close logic below only has to get itself
    // right — it doesn't need to know anything about the outer flyout.
    if (hasFlyout) {
        return (
            <div className="relative">
                {/* Bridges this row's parent list's own right padding
                    (`p-2` on the list wrapper below) — without this, hovering
                    that padding strip (still visibly inside the Report/Field
                    panel, just past this row's own right edge) reads as
                    "left the row" and dismisses the nested flyout the user
                    is trying to reach. `-mr-2`/`w-2` match that padding
                    exactly rather than a guessed pixel width, so this stays
                    correct if the padding token ever changes. */}
                <div
                    className="absolute inset-y-0 right-0 -mr-2 w-2"
                    onMouseEnter={hoverHandlers.onMouseEnter}
                    onMouseLeave={hoverHandlers.onMouseLeave}
                />
                <Popover
                    placement="right-start"
                    isOpen={isHovered}
                    onOpenChange={setIsHovered}
                    classNames={{
                        content: 'w-56 p-1.5',
                        // HeroUI's default PopoverTrigger styling shrinks and
                        // fades the trigger itself on open (`aria-expanded:
                        // scale-[0.97] opacity-70` — a press affordance meant for
                        // click-triggered popovers). For a hover-triggered one,
                        // that shrink moves this row's own edge out from under a
                        // cursor resting right at it: the row un-hovers, the
                        // popover starts closing, the row snaps back to full
                        // size, the cursor is inside it again, and it reopens —
                        // a self-sustaining blink for as long as the cursor sits
                        // near that edge. Neutralized here rather than globally,
                        // since a press affordance is still correct for an
                        // actually click-triggered popover elsewhere.
                        trigger: 'aria-expanded:scale-100 aria-expanded:opacity-100',
                    }}
                >
                    <PopoverTrigger>
                        <div
                            className={cn(rowClasses, 'cursor-pointer', {
                                // CSS `:hover` alone drops the highlight the
                                // instant the mouse crosses into the flyout (a
                                // separate, portaled element) — driving it off
                                // `isHovered` instead keeps the row visibly
                                // tied to its own open flyout.
                                'bg-[#F3F8FF]': isHovered,
                            })}
                            // Mouse only: the Popover moves focus into its content when it
                            // opens, so focus/blur handlers here closed it and reopened it
                            // in a loop (BT-2078). Keyboard opens it through onOpenChange.
                            onMouseEnter={hoverHandlers.onMouseEnter}
                            onMouseLeave={hoverHandlers.onMouseLeave}
                        >
                            {content}
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        onMouseEnter={hoverHandlers.onMouseEnter}
                        onMouseLeave={hoverHandlers.onMouseLeave}
                    >
                        <div className="flex w-full flex-col gap-0.5">
                            {(child.flyout ?? []).map((item) =>
                                item.comingSoon ? (
                                    <Tooltip
                                        key={item.name}
                                        content={DISABLED_TOOLTIP['coming-soon']}
                                        placement="right"
                                        size="sm"
                                        classNames={{
                                            content:
                                                'bg-[#192138] px-3 py-1 text-[11px] text-white',
                                        }}
                                    >
                                        <div
                                            aria-disabled
                                            className="flex cursor-default items-center gap-2 rounded-large px-2 py-1.5 text-[12px] font-medium text-default-300"
                                        >
                                            <Icon
                                                icon={`lucide:${item.icon}`}
                                                width={14}
                                                height={14}
                                                className="shrink-0 text-default-300"
                                            />
                                            <span className="flex-1 truncate">{item.name}</span>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Link
                                        key={item.name}
                                        href={item.href}
                                        onClick={onNavigate}
                                        className="flex items-center gap-2 rounded-large px-2 py-1.5 text-[12px] font-medium text-default-700 transition-colors hover:bg-[#F3F8FF]"
                                    >
                                        <Icon
                                            icon={`lucide:${item.icon}`}
                                            width={14}
                                            height={14}
                                            className="shrink-0 text-default-500"
                                        />
                                        <span className="flex-1 truncate">{item.name}</span>
                                    </Link>
                                ),
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        );
    }

    // A real destination that still wants roadmap/status context shown
    // (e.g. Builder, or Feasibility once resolved to internal-only) —
    // matches the expanded nav's own `NavChildRow` (`nav-section.tsx`).
    if (child.tooltip) {
        return (
            <Link href={child.href} onClick={onNavigate} className={rowClasses}>
                <Tooltip
                    content={child.tooltip}
                    placement="right"
                    size="sm"
                    classNames={{ content: 'bg-[#192138] px-3 py-1 text-[11px] text-white' }}
                >
                    {/* `w-full` so the label's `flex-1` still has the row's
                        full width to grow into and push the arrow to the
                        far edge — this div would otherwise shrink to fit
                        just the icon+label+arrow, same as `inert` below
                        (which never shows the arrow, so the bug is
                        invisible there). */}
                    <div className="flex w-full items-center gap-2.5">{content}</div>
                </Tooltip>
            </Link>
        );
    }

    return (
        <Link href={child.href} onClick={onNavigate} className={rowClasses}>
            {content}
        </Link>
    );
}
